'use client';

import { useToast } from '@common/hooks';
import { CustomError } from '@core/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'next/navigation';

import { AuthMutation } from '@/api';
import { INQUIRY_TYPE, INQUIRY_TYPE_LABEL } from '@/app/donation/constants';
import { PageNavigation } from '@/components/client';
import { useCustomRouter } from '@/hooks/useCustomRouter';

import { DesktopTitle } from '../../../_components/DesktopTitle';
import type { InquiryFormParams } from '../../_components/_hooks/useInquiryForm';
import { InquiryForm } from '../../_components/InquiryForm';

const InquiryPostingPage = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();
  const { back, replace } = useCustomRouter();

  const type = params?.type;
  const orderId = searchParams.get('orderId') ?? undefined;
  const orderProductId = searchParams.get('orderProductId')
    ? Number(searchParams.get('orderProductId'))
    : undefined;
  const productNumber = searchParams.get('productNumber') ?? undefined;
  const redirect = searchParams.get('redirect') ?? undefined;

  //* params validation
  switch (type) {
    case INQUIRY_TYPE.GIFT_PRODUCT:
      if (!productNumber)
        throw new CustomError({
          return_message: 'invalid access productNumber',
        });
      break;
    case INQUIRY_TYPE.DELIVERY:
      if (!orderId || !orderProductId || Number.isNaN(orderProductId))
        throw new CustomError({
          return_message: 'invalid access orderId or orderProductId',
        });
      break;
    case INQUIRY_TYPE.ETC:
      break;
    default:
      throw new CustomError({
        return_message: 'invalid access type',
      });
  }

  //* 문의 등록
  const { mutate: createInquiry, isPending: isCreateInquiryPending } =
    useMutation({
      ...AuthMutation['post:/wello/v2/auth/home-inquiry'](),
      onSuccess: () => {
        toast({
          message: '문의가 등록되었습니다.',
          type: 'success',
        });
        queryClient.invalidateQueries();

        if (redirect) {
          replace(redirect);
        } else {
          back();
        }
      },
      onError: (error) => {
        toastError({
          error,
        });
      },
    });

  //* 문의 등록 요청
  const handleSubmit = ({
    contents,
    fileUrlList,
    privateYN,
    title,
  }: InquiryFormParams) => {
    createInquiry({
      body: {
        contents,
        inquiryTypeCd: type,
        privateYN: privateYN,
        title,
        fileUrlList: fileUrlList,
        giftOrderProductId:
          type === INQUIRY_TYPE.DELIVERY ? orderProductId : undefined,
        productNumber:
          type === INQUIRY_TYPE.GIFT_PRODUCT ? productNumber : undefined,
      },
    });
  };

  const isLoadingForm = isCreateInquiryPending;

  return (
    <article className="flex flex-1 flex-col">
      <PageNavigation isNotUsedHomeButton />
      <DesktopTitle className="mb-20">
        {INQUIRY_TYPE_LABEL[type].label}
      </DesktopTitle>
      <InquiryForm
        type={type}
        orderId={orderId}
        loading={isLoadingForm}
        productNumber={productNumber}
        onSubmit={handleSubmit}
      />
    </article>
  );
};

export default InquiryPostingPage;
